import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/site/LegalPage";

export const Route = createFileRoute("/investor-disclaimer")({
  head: () => ({
    meta: [
      { title: "Investor Disclaimer — ClutchCube" },
      {
        name: "description",
        content:
          "Important disclosures regarding ClutchCube investor materials, forward-looking statements, and the risks of investing in early-stage companies.",
      },
      { property: "og:title", content: "Investor Disclaimer — ClutchCube" },
      {
        property: "og:description",
        content:
          "Important disclosures regarding ClutchCube investor materials, forward-looking statements, and the risks of investing in early-stage companies.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://clutchcube.com/investor-disclaimer" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://clutchcube.com/investor-disclaimer" }],
  }),
  component: InvestorDisclaimerPage,
});

function InvestorDisclaimerPage() {
  return (
    <LegalPage
      eyebrow="Investor Information"
      title="Investor Disclaimer"
      subtitle="Please read the following disclosures before reviewing any ClutchCube investor materials."
      updated="2026"
    >
      <div className="rounded-sm border border-primary/30 bg-card p-6 sm:p-8 shadow-elevated">
        <p className="text-[11px] uppercase tracking-[0.3em] text-primary mb-3">Important Notice</p>
        <p className="text-foreground leading-relaxed">
          This website and any accompanying materials are provided solely for informational purposes regarding ClutchCube
          and its planned business activities. Nothing on this website constitutes an offer to sell, or a solicitation of an
          offer to buy, any securities.
        </p>
      </div>

      <LegalSection title="Informational Purpose Only">
        <p>
          The information presented on ClutchCube.com is provided for general informational purposes only. It is not
          intended to be, and should not be construed as, financial, legal, tax, accounting, or investment advice.
        </p>
      </LegalSection>

      <LegalSection title="No Offer of Securities">
        <p>
          Nothing on this website constitutes an offer to sell securities or a solicitation of an offer to purchase
          securities. Any future investment opportunity in ClutchCube will only be made through definitive offering
          documents, if and when available, and in accordance with applicable federal and state securities laws.
        </p>
        <p>
          Access to the ClutchCube investor presentation, pitch deck, or other confidential materials does not constitute
          an investment offering. Materials are shared for evaluation and discussion purposes only.
        </p>
      </LegalSection>

      <LegalSection title="Forward-Looking Statements">
        <p>
          Statements about future events, projections, financial performance, growth plans, franchise expansion, and market
          opportunity are forward-looking statements. These statements involve known and unknown risks, uncertainties, and
          assumptions that may cause actual results to differ materially from those expressed or implied. ClutchCube
          undertakes no obligation to update forward-looking statements to reflect new information or future events.
        </p>
      </LegalSection>

      <LegalSection title="Risks of Early-Stage Investment">
        <p>
          Investments in early-stage and privately-held companies involve substantial risk, including the possible loss of
          the entire amount invested. Such investments are illiquid, may not produce returns, and are suitable only for
          investors who can bear the economic risk of loss.
        </p>
      </LegalSection>

      <LegalSection title="Independent Due Diligence">
        <p>
          Prospective investors are strongly encouraged to perform their own independent due diligence and to consult their
          own legal, tax, accounting, and financial advisors before making any investment decision. ClutchCube does not
          provide personalized investment advice or recommendations.
        </p>
      </LegalSection>

      <LegalSection title="Qualified Investors">
        <div className="rounded-sm border border-primary/30 bg-card p-6 sm:p-8 shadow-elevated">
          <p className="text-[11px] uppercase tracking-[0.3em] text-primary mb-3">
            Access Is Discretionary
          </p>
          <p className="text-foreground leading-relaxed">
            ClutchCube reserves the right, in its sole discretion, to determine who receives additional investment
            materials, participates in future fundraising discussions, or is invited to review confidential information.
            Submitting an investor inquiry does not create any obligation on the part of ClutchCube to share materials or
            pursue a transaction.
          </p>
        </div>
      </LegalSection>

      <LegalSection title="Confidentiality">
        <p>
          Investor materials provided by ClutchCube are confidential and intended solely for the recipient. They may not be
          reproduced, distributed, or disclosed, in whole or in part, without the prior written consent of ClutchCube.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          For investor relations inquiries, please use the{" "}
          <a href="/#invest" className="text-primary hover:underline">
            investor contact form
          </a>{" "}
          on this website.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
